import { useCallback, useEffect, useState } from 'react';
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    AccordionToggleEventHandler,
    MessageBar,
    MessageBarBody,
} from '@fluentui/react-components';

import { useSignInState } from './signInState';
import useFetch from '../Hooks/useFetchAPI';
import { isValidGuid } from '../Helpers/utils';

import ModuleConfigIntuneReporting from './ModuleConfigs/Module-IntuneReporting';
import ModuleWUfBReporting from './ModuleConfigs/Module-WUfBReporting';
import ModuleDellWarranty from './ModuleConfigs/Module-DellWarranty';
import ModuleLenovoWarranty from './ModuleConfigs/Module-LenovoWarranty';
import ModuleNISTAPI from './ModuleConfigs/Module-NISTConfig';
import ModuleSurfaceWarranty from './ModuleConfigs/Module-SurfaceWarranty';

const featureConfiguration = () => {
    const { signInState } = useSignInState();
    const { fetchAPI: fetchConfig, apiData, isLoading } = useFetch({ scope: 'AdminUser' });

    const [openItems, setOpenItems] = useState(["0"]);
    const [tenantConfigured, setTenantConfigured] = useState<boolean | null>(null);

    const handleToggle: AccordionToggleEventHandler<string> = (event, data) => {
        setOpenItems(data.openItems);
    };

    const handleTenantConfiguredChange = useCallback((configured: boolean) => {
        setTenantConfigured(configured);
    }, []);

    useEffect(() => {
        setOpenItems(["0"]);
        setTenantConfigured(null);

        const loadTenantId = async () => {
            await fetchConfig({ url: 'admin/getfeatureconfig?featureName=IntuneReporting', method: 'GET' });
        };

        loadTenantId();
    }, [signInState.currentCustomerID]);

    useEffect(() => {
        if (isLoading || !apiData) {
            return;
        }

        const result = (apiData as { result?: { TenantID?: string }[] }).result;
        const tenantId = result?.[0]?.TenantID ?? '';
        setTenantConfigured(isValidGuid(tenantId));
    }, [apiData, isLoading]);

    return (
        <div style={{ marginLeft: '20px' }}>
            {tenantConfigured === false && (
                <MessageBar intent="info" style={{ marginBottom: '16px', maxWidth: '1200px' }}>
                    <MessageBarBody>
                        Configure a valid Entra tenant id in the Intune &amp; Entra Reporting section
                        before enabling data sync for any connector.
                    </MessageBarBody>
                </MessageBar>
            )}
            <Accordion
                openItems={openItems}
                onToggle={handleToggle}
                collapsible
            >
                <AccordionItem value="1">
                    <AccordionHeader>Intune & Entra Reporting Configuration</AccordionHeader>
                    <AccordionPanel>
                        <ModuleConfigIntuneReporting onTenantConfiguredChange={handleTenantConfiguredChange} />
                    </AccordionPanel>
                </AccordionItem>
                <AccordionItem value="2">
                    <AccordionHeader>WUfB Reporting Configuration</AccordionHeader>
                    <AccordionPanel>
                        <ModuleWUfBReporting tenantConfigured={tenantConfigured === true} />
                    </AccordionPanel>
                </AccordionItem>
                <AccordionItem value="3">
                    <AccordionHeader>Dell Warranty Configuration</AccordionHeader>
                    <AccordionPanel>
                        <ModuleDellWarranty tenantConfigured={tenantConfigured === true} />
                    </AccordionPanel>
                </AccordionItem>
                <AccordionItem value="4">
                    <AccordionHeader>Lenovo Warranty Configuration</AccordionHeader>
                    <AccordionPanel>
                        <ModuleLenovoWarranty tenantConfigured={tenantConfigured === true} />
                    </AccordionPanel>
                </AccordionItem>
                <AccordionItem value="surface-warranty">
                    <AccordionHeader>Surface Warranty Configuration</AccordionHeader>
                    <AccordionPanel>
                        <ModuleSurfaceWarranty tenantConfigured={tenantConfigured === true} />
                    </AccordionPanel>
                </AccordionItem>
                <AccordionItem value="5">
                    <AccordionHeader>NIST API Configuration</AccordionHeader>
                    <AccordionPanel>
                        <ModuleNISTAPI tenantConfigured={tenantConfigured === true} />
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>
        </div>
    );
}

export default featureConfiguration;
